import { useState, useEffect, useRef } from "react";
import { ColorPicker } from "antd";

// El ColorPicker de antd devuelve el string en el formato que esté activo en
// el selector (hex/rgb/hsb) según lo que haya tocado el usuario, no siempre hex.
// Normalizamos siempre a hex (salvo gradientes) para no guardar "rgb(...)" en DB,
// formato que el resto del frontend (hexAlpha, getContrastColor) no soporta.
function colorPickerCss(raw, css) {
  if (typeof css === "string" && css.includes("gradient")) return css;
  if (raw && typeof raw.toHexString === "function") return raw.toHexString();
  return css;
}

export const FONTS = [
  { value:"'Inter', system-ui, sans-serif",         label:"Inter" },
  { value:"'Roboto', sans-serif",                   label:"Roboto" },
  { value:"'Montserrat', sans-serif",               label:"Montserrat" },
  { value:"'Poppins', sans-serif",                  label:"Poppins" },
  { value:"'Lato', sans-serif",                     label:"Lato" },
  { value:"'Raleway', sans-serif",                  label:"Raleway" },
  { value:"'Playfair Display', serif",              label:"Playfair Display" },
  { value:"'Merriweather', serif",                  label:"Merriweather" },
  { value:"Georgia, serif",                         label:"Georgia" },
  { value:"'Courier New', monospace",               label:"Courier New" },
];

export const T = {
  surf: "#ffffff", bdr: "#e0e4f0", bdrF: "var(--store-primary, #2558d9)",
  txt: "#0b0f1c", sub: "#5a6278", mut: "#9099b0",
  blue: "var(--store-primary, #2558d9)", blueBg: "var(--store-primary-bg, rgba(37,88,217,0.08))",
  ok: "#059669", okBg: "#d1fae5", danger: "#dc2626",
  rs: "7px", sh: "0 1px 3px rgba(0,0,0,0.06)",
  sidebar: "#f7f8fc",
};

export function Toggle({ on, onChange }) {
  return (
    <button type="button" onClick={e => { e.stopPropagation(); onChange(!on); }}
      style={{ width:40,height:22,borderRadius:11,flexShrink:0,padding:0,border:"none",
        outline:"none",cursor:"pointer",background:on?T.blue:"#c8d0e0",position:"relative",transition:"background 0.2s" }}>
      <span style={{ display:"block",position:"absolute",top:2,left:on?20:2,width:18,height:18,
        borderRadius:"50%",background:"#fff",boxShadow:"0 1px 3px rgba(0,0,0,0.22)",
        transition:"left 0.18s cubic-bezier(.25,.1,.25,1)" }}/>
    </button>
  );
}

export function TInput({ value, onChange, placeholder, type="text" }) {
  const [f,setF] = useState(false);
  return (
    <input type={type} value={value} onChange={e=>onChange(e.target.value)}
      placeholder={placeholder} onFocus={()=>setF(true)} onBlur={()=>setF(false)}
      style={{ width:"100%",padding:"8px 11px",fontSize:13,color:T.txt,fontFamily:"inherit",
        border:`1.5px solid ${f?T.bdrF:T.bdr}`,borderRadius:T.rs,
        background:f?"#fff":"#fafbfe",outline:"none",
        boxShadow:f?`0 0 0 3px ${T.blueBg}`:"none",transition:"all 0.15s" }}/>
  );
}

export function TArea({ value, onChange, placeholder, rows=3 }) {
  const [f,setF] = useState(false);
  return (
    <textarea value={value} onChange={e=>onChange(e.target.value)}
      placeholder={placeholder} rows={rows} onFocus={()=>setF(true)} onBlur={()=>setF(false)}
      style={{ width:"100%",padding:"8px 11px",fontSize:13,color:T.txt,fontFamily:"inherit",
        lineHeight:1.65,resize:"vertical",
        border:`1.5px solid ${f?T.bdrF:T.bdr}`,borderRadius:T.rs,
        background:f?"#fff":"#fafbfe",outline:"none",
        boxShadow:f?`0 0 0 3px ${T.blueBg}`:"none",transition:"all 0.15s" }}/>
  );
}

export function FieldLabel({ children }) {
  return <div style={{ fontSize:9.5,fontWeight:700,color:T.mut,letterSpacing:.8,
    textTransform:"uppercase",marginBottom:5 }}>{children}</div>;
}

export function Field({ label, children }) {
  return <div style={{ marginBottom:14 }}><FieldLabel>{label}</FieldLabel>{children}</div>;
}

function useColorRaw(value) {
  const [raw, setRaw] = useState(undefined);
  return [raw ?? (value || "#ffffff"), setRaw];
}

export function ColorChip({ label, value, onChange, placeholder }) {
  const [pickerVal, setRaw] = useColorRaw(value);
  return (
    <div title={label} style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:4 }}>
      <ColorPicker
        size="small"
        value={pickerVal}
        mode={["single","gradient"]}
        onChange={(raw, css) => { setRaw(raw); onChange(colorPickerCss(raw, css)); }}
      />
      <span style={{ fontSize:9,color:T.mut,whiteSpace:"nowrap",textAlign:"center",maxWidth:70,lineHeight:1.2 }}>
        {value ? label : (placeholder || label)}
      </span>
    </div>
  );
}

export function ColorRow({ label, value, onChange, children }) {
  const [pickerVal, setRaw] = useColorRaw(value);
  if (children) {
    return (
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",
        gap:10,padding:"10px 0",borderBottom:`1px solid ${T.bdr}` }}>
        {label && <span style={{ fontSize:13,color:T.txt }}>{label}</span>}
        <div style={{ display:"flex",alignItems:"center",gap:8,marginLeft:label ? "auto" : 0 }}>
          {children}
        </div>
      </div>
    );
  }
  const isGrad = value?.includes("gradient");
  const preview = isGrad
    ? { background: value }
    : { background: value || "#ffffff" };
  return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",
      padding:"10px 0",borderBottom:`1px solid ${T.bdr}` }}>
      <span style={{ fontSize:13,color:T.txt }}>{label}</span>
      <div style={{ display:"flex",alignItems:"center",gap:10 }}>
        <div style={{ width:28,height:28,borderRadius:7,...preview,
          border:"2px solid rgba(0,0,0,0.12)",boxShadow:"0 1px 3px rgba(0,0,0,0.08)",flexShrink:0 }}/>
        {!isGrad && <span style={{ fontSize:12,color:T.sub,fontFamily:"monospace" }}>
          {(value||"#ffffff").toUpperCase()}
        </span>}
        <ColorPicker
          size="small"
          value={pickerVal}
          mode={["single","gradient"]}
          onChange={(raw, css) => { setRaw(raw); onChange(colorPickerCss(raw, css)); }}
        />
      </div>
    </div>
  );
}

// Input de WhatsApp con dos modos: Argentina (prefijo fijo +549, el usuario sólo
// carga el código de área + número) o internacional (número completo con código
// de país, para dueños de tienda con número de afuera, ej. +1 630-448-0120).
export function WhatsAppNumberInput({ value, onChange, placeholder = "3400000000" }) {
  const [mode, setMode] = useState("ar");
  const [f, setF] = useState(false);
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || !value) return;
    hydrated.current = true;
    setMode(value.startsWith("549") ? "ar" : "intl");
  }, [value]);

  const raw = value || "";
  const arRest = raw.startsWith("549") ? raw.slice(3) : raw;

  return (
    <div>
      <div style={{ display:"flex",gap:6,marginBottom:8 }}>
        {[{ id:"ar", label:"🇦🇷 Argentina" }, { id:"intl", label:"🌎 Otro país" }].map(({ id, label }) => (
          <button key={id} type="button" onClick={() => setMode(id)}
            style={{ padding:"5px 10px",fontSize:11.5,fontWeight:600,borderRadius:6,cursor:"pointer",
              border:`1.5px solid ${mode===id?T.blue:T.bdr}`,
              background:mode===id?T.blueBg:"#fafbfe",color:mode===id?T.blue:T.sub,
              transition:"all 0.15s" }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display:"flex",alignItems:"stretch",border:`1.5px solid ${f?T.bdrF:T.bdr}`,
        borderRadius:T.rs,overflow:"hidden",background:f?"#fff":"#fafbfe",
        boxShadow:f?`0 0 0 3px ${T.blueBg}`:"none",transition:"all 0.15s" }}>
        <div style={{ display:"flex",alignItems:"center",gap:6,padding:"8px 11px",
          background:"#eef0f8",borderRight:`1.5px solid ${T.bdr}`,fontSize:13,
          color:T.sub,fontWeight:600,whiteSpace:"nowrap",flexShrink:0 }}>
          {mode==="ar" ? "🇦🇷 +549" : "🌎 +"}
        </div>
        {mode==="ar" ? (
          <input type="tel" value={arRest} placeholder={placeholder}
            onFocus={()=>setF(true)} onBlur={()=>setF(false)}
            onChange={e => onChange("549" + e.target.value.replace(/\D/g, ""))}
            style={{ flex:1,minWidth:0,padding:"8px 11px",fontSize:13,color:T.txt,
              fontFamily:"inherit",border:"none",outline:"none",background:"transparent" }}/>
        ) : (
          <input type="tel" value={raw} placeholder="16304480120"
            onFocus={()=>setF(true)} onBlur={()=>setF(false)}
            onChange={e => onChange(e.target.value.replace(/\D/g, ""))}
            style={{ flex:1,minWidth:0,padding:"8px 11px",fontSize:13,color:T.txt,
              fontFamily:"inherit",border:"none",outline:"none",background:"transparent" }}/>
        )}
      </div>
      <div style={{ fontSize:10.5,color:T.mut,marginTop:4 }}>
        {mode==="ar"
          ? "Se agrega automáticamente el +549."
          : "Ingresá el número completo con código de país (sin el +)."}
      </div>
    </div>
  );
}

export function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{ width:"100%",padding:"8px 11px",fontSize:13,color:T.txt,fontFamily:"inherit",
        border:`1.5px solid ${T.bdr}`,borderRadius:T.rs,background:"#fafbfe",outline:"none",cursor:"pointer" }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function TextStyleRow({ label, style, onChange, defaultColor }) {
  const s = style || {};
  return (
    <div style={{ display:"flex",alignItems:"center",gap:6,marginTop:4,padding:"4px 0",
      borderTop:`1px dashed ${T.bdr}` }}>
      {label && (
        <span style={{ fontSize:9,color:T.mut,textTransform:"uppercase",fontWeight:700,
          letterSpacing:.6,flexShrink:0,minWidth:32 }}>{label}</span>
      )}
      <select value={s.fontFamily||""} onChange={e => onChange({ ...s, fontFamily:e.target.value||undefined })}
        style={{ flex:1,padding:"3px 6px",fontSize:11,border:`1px solid ${T.bdr}`,borderRadius:5,
          color:T.txt,background:"#fafbfe",cursor:"pointer",fontFamily:s.fontFamily||"inherit" }}>
        <option value="">Sistema (por defecto)</option>
        {FONTS.map(f => (
          <option key={f.value} value={f.value} style={{ fontFamily:f.value }}>{f.label}</option>
        ))}
      </select>
      <input type="number" value={s.fontSize||""} placeholder="px" min={8} max={120}
        onChange={e => onChange({ ...s, fontSize:e.target.value ? Number(e.target.value) : undefined })}
        style={{ width:48,padding:"3px 5px",fontSize:11,border:`1px solid ${T.bdr}`,borderRadius:5,
          textAlign:"center",color:T.txt,background:"#fafbfe" }}/>
      <div style={{ display:"flex",alignItems:"center",gap:3,flexShrink:0 }}>
        <ColorPicker
          size="small"
          value={(s.colorRaw && typeof s.colorRaw.toHexString === "function" ? s.colorRaw : null) ?? (s.color || defaultColor || "#000000")}
          mode={["single","gradient"]}
          onChange={(raw, css) => onChange({ ...s, color: colorPickerCss(raw, css), colorRaw: raw })}
        />
        {s.color && (
          <button onClick={() => onChange({ ...s, color:undefined, colorRaw:undefined })}
            style={{ fontSize:11,color:T.mut,background:"none",border:"none",
              cursor:"pointer",padding:0,lineHeight:1,flexShrink:0 }}>×</button>
        )}
      </div>
    </div>
  );
}

export function SectionPlaceholder({ title, description }) {
  return (
    <div style={{ padding:"32px 24px",textAlign:"center" }}>
      <div style={{ fontSize:32,marginBottom:12 }}>🚧</div>
      <div style={{ fontSize:15,fontWeight:700,color:T.txt,marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:13,color:T.mut,lineHeight:1.6,maxWidth:300,margin:"0 auto" }}>{description}</div>
    </div>
  );
}
